// App.jsx
import React, { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import i18n from "./i18n";
import Home from "./pages/Home";
import About from "./pages/About";
import ArticleSupply from "./pages/ArticleSupply";
import DocDetail from "./components/DocDetail";
import RegistryPage from "./pages/RegistryPage";
import CoursesPage from "./pages/CoursesPage";
import MarketplacePage from "./pages/MarketplacePage";
import BookPage from "./pages/BookPage";
import MainUserSide from "./components/MainUserSide";
import ResetPassword from "./pages/ResetPassword";
import ForgotPassword from "./pages/ForgotPassword";
import { RequireAuth } from "./components/RequireAuth";
import { UserAccountLayout } from "./components/UserAccountLayout";
import { ModeratorLayout } from "./components/ModeratorLayout";
import { HelmetProvider } from "react-helmet-async";

const ArticleProduction = React.lazy(() => import("./pages/ArticleProduction"));
const ArticleMoney = React.lazy(() => import("./pages/ArticleMoney"));

const SUPPORTED = ["ru", "en", "es", "zh"];
const LANG_RE = /^\/(ru|en|es|zh)(\/|$)/i;

// определяем дефолтный язык
function detectDefault() {
  const saved = localStorage.getItem("lang");
  if (saved && SUPPORTED.includes(saved)) return saved;
  const nav = navigator.language?.slice(0, 2);
  if (SUPPORTED.includes(nav)) return nav;
  return "ru";
}

// Инъектор языка: добавляет /{lang} к любому пути без него
function LanguageGateway() {
  const loc = useLocation();
  const hasLang = LANG_RE.test(loc.pathname);
  if (!hasLang) {
    const lang = detectDefault();
    // обновим <html lang="">
    if (document?.documentElement) document.documentElement.lang = lang;
    if (i18n.language !== lang) i18n.changeLanguage(lang);

    // редирект, сохраняя путь, query и hash
    return (
      <Navigate
        replace
        to={`/${lang}${loc.pathname}${loc.search}${loc.hash}`}
      />
    );
  }
  // уже есть язык — дальше рендерим вложенные маршруты
  return <Outlet />;
}

// Гард: синхронизирует i18n, если язык уже в пути
function LangGuard({ children }) {
  const loc = useLocation();
  const m = loc.pathname.match(LANG_RE);
  const lang = (m?.[1] || "ru").toLowerCase();
  if (i18n.language !== lang) i18n.changeLanguage(lang);
  if (document?.documentElement) document.documentElement.lang = lang;
  return children;
}

export default function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <Routes>
            {/* Любой путь сначала проходит через LanguageGateway */}
            <Route path="/*" element={<LanguageGateway />}>
              {/* Все маршруты с языковым префиксом */}
              <Route
                path=":lang"
                element={
                  <LangGuard>
                    <Home />
                  </LangGuard>
                }
              />
              <Route
                path=":lang/about"
                element={
                  <LangGuard>
                    <About />
                  </LangGuard>
                }
              />
              <Route
                path=":lang/registry"
                element={
                  <LangGuard>
                    <RegistryPage />
                  </LangGuard>
                }
              />
              <Route
                path=":lang/courses"
                element={
                  <LangGuard>
                    <CoursesPage />
                  </LangGuard>
                }
              />
              <Route
                path=":lang/marketplace"
                element={
                  <LangGuard>
                    <MarketplacePage />
                  </LangGuard>
                }
              />
              <Route
                path=":lang/book"
                element={
                  <LangGuard>
                    <BookPage />
                  </LangGuard>
                }
              />

              {/* Ленивая загрузка статей */}
              <Route
                path=":lang/prod-article"
                element={
                  <LangGuard>
                    <Suspense fallback={<div>Loading...</div>}>
                      <ArticleProduction />
                    </Suspense>
                  </LangGuard>
                }
              />
              <Route
                path=":lang/money-article"
                element={
                  <LangGuard>
                    <Suspense fallback={<div>Loading...</div>}>
                      <ArticleMoney />
                    </Suspense>
                  </LangGuard>
                }
              />
              <Route
                path=":lang/supply-article"
                element={
                  <LangGuard>
                    <Suspense fallback={<div>Loading...</div>}>
                      <ArticleSupply />
                    </Suspense>
                  </LangGuard>
                }
              />

              {/* Аутентификация */}
              <Route path=":lang/login" element={<MainUserSide />} />
              <Route path=":lang/register" element={<MainUserSide />} />
              <Route
                path=":lang/account/*"
                element={
                  <RequireAuth>
                    <MainUserSide />
                  </RequireAuth>
                }
              >
                <Route index element={<UserAccountLayout />} />
                <Route path="moderator" element={<ModeratorLayout />} />
              </Route>
              <Route path=":lang/reset-password" element={<ResetPassword />} />
              <Route
                path=":lang/forgot-password"
                element={<ForgotPassword />}
              />
              <Route
                path=":lang/doc/:id"
                element={
                  <LangGuard>
                    <DocDetail />
                  </LangGuard>
                }
              />

              {/* Фоллбек (если почему-то не совпало) */}
              <Route path="*" element={<Navigate to="/ru" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}
